'use strict'

import * as coreCommon from 'assets/core/js/common'
import SliderManager from 'assets/core/js/module/sliderManager'
import ElementMover from 'assets/core/js/module/elementMover'
import { PartialsEvents } from 'assets/core/js/module/partialsManager'
import { initTabs } from 'assets/core/js/module/paymentFacilities'
import { hideLoading } from 'assets/core/js/module/buttonLoading'
import SidePanel from '@ui/SidePanel/component'
import CheckoutAccess from '@ui/Accommodation/CheckoutAccess/component'
import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import { initZoomMediasPanel } from 'assets/core/js/page/product/zoomMediasPanel'

import facets from './facets'

import type { SidePanelType } from '@ui/SidePanel/component'

const productSidePanel: {
  accommodationSidePanelIds?: string[]
  init: () => void
  initAccommodationSidePanel: () => void
  initSearchFiltersSidePanel: () => void
} = {
  init: function () {
    this.accommodationSidePanelIds = []

    this.initAccommodationSidePanel()
    this.initSearchFiltersSidePanel()

    document.body.addEventListener('close-all-modals', () => {
      document.querySelectorAll<HTMLElement>('.accommodation__side-panel').forEach((el) => {
        ElementPropertiesManager.getProperty<SidePanelType>(el, 'sidePanel')?.close()
      })
    })

    document.querySelector('[data-partial-id="results"]')?.addEventListener(PartialsEvents.POST_UPDATE, () => {
      this.accommodationSidePanelIds?.forEach((id) => {
        const sidePanelEl = document.getElementById(id)

        if (!sidePanelEl) {
          return
        }

        if (ElementPropertiesManager.hasProperty(sidePanelEl, 'sidePanel'))
          ElementPropertiesManager.getProperty<SidePanelType>(sidePanelEl, 'sidePanel')?.destroy()
      })
      this.accommodationSidePanelIds = []
    })
  },

  initAccommodationSidePanel: function () {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this

    const toggleSidePanelFn = function (this: HTMLElement, e: MouseEvent): void {
      const resultEl = this.closest('.result')
      const target = e.target as HTMLElement
      let sidePanel: SidePanelType | undefined

      if (resultEl) {
        const sidePanelId = resultEl.getAttribute('data-sidepanel-id') as string
        const sidePanelUrl = resultEl.getAttribute('data-sidepanel-url') as string
        const accommodationId = resultEl.getAttribute('data-id')

        const sidePanelExists = document.getElementById(sidePanelId)
          ? ElementPropertiesManager.hasProperty(document.getElementById(sidePanelId) as HTMLElement, 'sidePanel')
          : false

        if (!sidePanelExists) {
          const templateEl = document.getElementById(`${sidePanelId}-template`) as HTMLElement

          if (!templateEl) {
            return
          }

          const sidePanelEl = templateEl.parentNode?.insertBefore<HTMLElement>(coreCommon.createNodeFromHTML(templateEl.innerHTML), templateEl)

          if (!sidePanelEl) {
            return
          }

          sidePanel = SidePanel(sidePanelEl, {
            onOpen: function () {
              document.body.dispatchEvent(new CustomEvent('sidePanelAccommodation.modal.opened', { detail: { accommodationId } }))
            },
            onClose: function (sidePanel) {
              document.body.dispatchEvent(new CustomEvent('sidePanelAccommodation.modal.closed'))

              const checkOfferForm = sidePanel.getElement().querySelector<HTMLFormElement>('form[data-check-offer-id]')

              if (checkOfferForm) {
                const submitEl = checkOfferForm.querySelector<HTMLButtonElement>('[type="submit"]')

                if (!submitEl) {
                  return
                }

                if (!submitEl.classList.contains('dca-button--with-loading')) {
                  return
                }

                hideLoading(submitEl)
              }
            },
            onContentSet: function (sidePanel) {
              const images = sidePanel.getElement().querySelectorAll<HTMLImageElement>('.side-panel__images img')
              const sliderEl = sidePanel.getElement()?.querySelector<HTMLElement>('.swiper')

              if (sliderEl && !sliderEl.classList.contains('swiper-horizontal')) {
                SliderManager.initSlider(sliderEl, {
                  navigation: {
                    nextEl: sliderEl.querySelector('.swiper-button-next') as HTMLElement,
                    prevEl: sliderEl.querySelector('.swiper-button-prev') as HTMLElement,
                  },
                  pagination: {
                    clickable: true,
                    el: sliderEl.querySelector('.swiper-pagination') as HTMLElement,
                  },
                  slidesPerView: 'auto',
                  loop: document.querySelectorAll(`#${sidePanelId} .swiper-slide`).length > 1,
                  spaceBetween: 16,
                  on: {
                    init: function () {
                      coreCommon.initLazyLoading(`#${sidePanelId} .lazy`)
                    },
                  },
                })

                images.forEach((img) => {
                  initZoomMediasPanel(img, true)
                })
              }

              const checkOfferForm = sidePanel.getElement()?.querySelector<HTMLFormElement>('form[data-check-offer-id]')
              if (checkOfferForm) {
                CheckoutAccess(checkOfferForm)

                checkOfferForm.addEventListener('submit', function () {
                  document.body.dispatchEvent(new CustomEvent('checkOfferForm.submit', { detail: { accommodationId } }))
                })
              }
            },
          })

          ElementPropertiesManager.addProperty(sidePanelEl, 'sidePanel', sidePanel)

          templateEl.remove()

          const xhrRequest = coreCommon.createXhr('GET', sidePanelUrl)

          xhrRequest.onreadystatechange = () => {
            if (xhrRequest.readyState === XMLHttpRequest.DONE && xhrRequest.status === 200 && xhrRequest.response) {
              sidePanel?.setContent(xhrRequest.response as string)
              initTabs(sidePanel?.getElement() as HTMLElement)

              self.accommodationSidePanelIds?.push(sidePanelId)
            } else if (xhrRequest.readyState === XMLHttpRequest.DONE && xhrRequest.status === 500) {
              sidePanel?.close()
              window.setTimeout(() => sidePanel?.destroy(), 250)
            }
          }

          xhrRequest.send()
        }

        sidePanel = ElementPropertiesManager.getProperty<SidePanelType>(document.getElementById(sidePanelId) as HTMLElement, 'sidePanel')

        const parentNode = target.parentNode as Element

        if (
          sidePanel &&
          !sidePanel.getElement().contains(target) &&
          !['A', 'BUTTON'].includes(target.nodeName) &&
          !target.classList.contains('open-calendar') &&
          !target.hasAttribute('data-check-offer') &&
          !target.classList.contains('button__availability') &&
          !target.classList.contains('open-camping-map') &&
          !parentNode.classList.contains('dca-tag')
        ) {
          sidePanel?.open()
        }
      }
    }

    const resultsListEl = document.getElementById('results-list')

    if (resultsListEl) {
      resultsListEl.addEventListener('click', function (e) {
        const target = e.target as HTMLElement

        if (
          !target.classList.contains('result-accommodation__plan-button') &&
          (target.closest('.result .result__details') || (target.closest('.result__image') && !target.closest('.swiper-button')))
        ) {
          toggleSidePanelFn.call(target, e)
        }
      })
    }
  },

  initSearchFiltersSidePanel: function () {
    const searchEl = document.getElementById('search-availability-form') as HTMLFormElement
    const filtersEl = document.getElementById('filter-accommodations-form') as HTMLElement
    const searchSubmitEl = document.getElementById('search-filters-on-offers')?.querySelector('[type="submit"]') as HTMLElement
    const filtersSubmitEl = document.getElementById('accommodation_filters_filter') as HTMLElement
    const filtersResetEl = document.getElementById('filter-accommodations-form-reset') as HTMLElement

    const sidePanelEl = document.getElementById('search-filters-side-panel')

    if (!sidePanelEl) {
      return
    }

    let sidePanel: SidePanelType | undefined
    const sidePanelExists = ElementPropertiesManager.hasProperty(sidePanelEl, 'sidePanel')

    if (!sidePanelExists) {
      sidePanel = SidePanel(sidePanelEl, {
        onOpen: function (sidePanel) {
          ElementMover.move(
            'search',
            searchEl,
            sidePanel.getElement()?.querySelector('.side-panel__content .content__block[data-content="search"]') as Element
          )
          ElementMover.move(
            'filters',
            filtersEl,
            sidePanel.getElement()?.querySelector('.side-panel__content .content__block[data-content="filters"]') as Element
          )
          ElementMover.move(
            'search-submit',
            searchSubmitEl,
            sidePanel.getElement()?.querySelector('.side-panel__bottom .actions__block[data-content="search"]') as Element
          )
          ElementMover.move(
            'filters-reset',
            filtersResetEl,
            sidePanel.getElement()?.querySelector('.side-panel__bottom .actions__block[data-content="filters"]') as Element
          )
          ElementMover.move(
            'filters-submit',
            filtersSubmitEl,
            sidePanel.getElement()?.querySelector('.side-panel__bottom .actions__block[data-content="filters"]') as Element
          )
        },
        onClose: function () {
          // reverse order of moved elements so we don't have missing nodes when repositionning elements
          ElementMover.restore('filters-submit')
          ElementMover.restore('filters-reset')
          ElementMover.restore('search-submit')
          ElementMover.restore('filters')
          ElementMover.restore('search')

          if (activeTabEl) {
            const event = activeTabEl.getAttribute('data-target') === 'search' ? 'search' : 'filter'
            document.body.dispatchEvent(new CustomEvent(`product-page.${event}.modal.closed`))

            activeTabEl.classList.remove('tab__item--selected')
            activeContentEl?.classList.remove('visible')
            activeBottomEl?.classList.remove('visible')

            activeTabEl = null
          }
        },
      })

      ElementPropertiesManager.addProperty(sidePanelEl, 'sidePanel', sidePanel)
    }

    sidePanel = ElementPropertiesManager.getProperty<SidePanelType>(sidePanelEl, 'sidePanel')

    let activeTabEl = sidePanel?.getElement()?.querySelector('.tab__item--selected')
    let activeContentEl = sidePanel?.getElement()?.querySelector('.content__block.visible')
    let activeBottomEl = sidePanel?.getElement()?.querySelector('.actions__block.visible')

    sidePanel
      ?.getElement()
      ?.querySelectorAll('.tab__item')
      .forEach(function (el) {
        el.addEventListener('click', function (this: HTMLElement) {
          if (activeTabEl) {
            activeTabEl.classList.remove('tab__item--selected')
            activeContentEl?.classList.remove('visible')
            activeBottomEl?.classList.remove('visible')
          }

          const target = this.getAttribute('data-target') as string

          if (!activeTabEl || activeTabEl.getAttribute('data-target') !== target) {
            const event = target === 'search' ? 'search' : 'filter'
            document.body.dispatchEvent(new CustomEvent(`product-page.${event}.modal.opened`))
          }

          this.classList.add('tab__item--selected')

          // eslint-disable-next-line @typescript-eslint/no-this-alias
          activeTabEl = this
          activeContentEl = sidePanel?.getElement()?.querySelector(`.content__block[data-content="${target}"]`)
          activeBottomEl = sidePanel?.getElement()?.querySelector(`.actions__block[data-content="${target}"]`)

          activeTabEl.classList.add('tab__item--selected')
          activeContentEl?.classList.add('visible')
          activeBottomEl?.classList.add('visible')
        })
      })

    filtersResetEl.addEventListener('click', function (e) {
      e.preventDefault()

      Array.from(document.querySelectorAll<HTMLInputElement>('input[name="accommodation_filters[propertiesFilter][]"]:checked')).map((checkbox) => {
        checkbox.checked = false

        if ('createEvent' in document) {
          const evt = document.createEvent('HTMLEvents')
          evt.initEvent('change', false, true)
          checkbox.dispatchEvent(evt)
        } else {
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          checkbox.fireEvent('onchange')
        }

        return true
      })

      facets.sortAccommodations()
    })

    const sortResultsFn = function (e: MouseEvent): void {
      e.preventDefault()

      if (sidePanel) {
        sidePanel.close()
      }
      facets.sortAccommodations()
    }

    searchSubmitEl.addEventListener('click', sortResultsFn)
    filtersSubmitEl.addEventListener('click', sortResultsFn)
  },
}

export default productSidePanel
